// ---------- ce que l'écran apprend au début ----------
// Un module à part et non un coin de ui.ts : ce qu'il tient n'est pas de l'état de jeu mais de
// l'état d'APPRENTISSAGE, et il doit pouvoir être débranché d'une ligne le jour où on n'en veut
// plus. Ses cinq points d'accroche dans le reste du code sont autant d'appels à sens unique — rien
// ici ne rappelle ui.ts ni render.ts, donc rien ne ferme de cycle d'import.
//
// Trois leçons : le tap, le balayage, la boussole. Les deux premières se donnent ensemble, dans
// UN dialogue à la toute première feuille (src/gestureDialog.ts) ; la troisième reste une bulle.
//
// Il y avait trois bulles, et la règle qui les tenait était que **le bouton qui valide une leçon,
// c'est le geste lui-même** : aucune n'avait de « J'ai compris » à taper. Elle a marché pour la
// boussole, pas pour le balayage. Sa bulle attendait trois zones tapées une par une pour parler et
// arrivait en pleine partie, quand personne ne lit : les nouveaux joueurs passaient à côté et
// coloriaient toute leur feuille au tap (constaté en septembre 2026). Les bulles du tap et du
// balayage ont donc été retirées au profit du dialogue, qui a un bouton — c'est le prix d'un
// écran qui passe AVANT la première case. La boussole n'a pas ce problème : sa bulle parle au
// moment exact où la flèche est là, et le geste qu'elle demande la fait disparaître.
//
// Ce n'est PAS un réglage : rien ici ne passe par Settings. Une leçon apprise n'est pas une
// préférence, et la liste blanche de loadSettings() n'aurait pas à connaître trois drapeaux qui ne
// survivent qu'aux premières parties.

import { anim, noMotion } from './anim';
import { openGestureDialog } from './gestureDialog';
import { t } from './i18n';
import { progressIndex } from './progress';
import { S } from './state';

type Lecon = 'poser' | 'balayer' | 'boussole';

const KEY = 'cg.coach';
const LECONS: readonly Lecon[] = ['poser', 'balayer', 'boussole'];

/** La boussole doit être RESTÉE à l'écran : elle apparaît et disparaît au fil des mouvements de
 *  caméra, et une bulle qui suivrait chaque scintillement serait un clignotant. */
const BOUSSOLE_MS = 600;
/** Le dialogue des deux gestes laisse le temps d'arriver sur la feuille avant de la couvrir : le
 *  joueur doit avoir vu à quoi ressemble ce qu'on va lui apprendre à remplir. */
const POSER_MS = 1200;
/** La boussole renonce à sa deuxième apparition sans tap : deux fois ignorée, c'est un choix. */
const BOUSSOLE_APPARITIONS = 2;

/** Les leçons acquises. Gardées comme la progression : un quota plein ou un navigateur en
 *  navigation privée coûte la mémoire de la leçon, jamais la partie. */
let acquis: Record<Lecon, boolean> = lire();

function lire(): Record<Lecon, boolean> {
  const vide = { poser: false, balayer: false, boussole: false };
  try {
    const brut = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<
      Record<Lecon, boolean>
    > | null;
    if (!brut || typeof brut !== 'object') return vide;
    for (const l of LECONS) if (brut[l] === true) vide[l] = true;
    return vide;
  } catch {
    return vide;
  }
}

function ecrire(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(acquis));
  } catch {
    /* on joue, sans s'en souvenir */
  }
}

// ---------- l'état d'une session ----------
/** La leçon à l'écran. UNE SEULE À LA FOIS : deux conseils simultanés, c'est un conseil que
 *  personne ne lit. La première arrivée garde la place. */
let visible: Lecon | null = null;
let elt: HTMLElement | null = null;
let minuteur = 0;
/** Depuis quand la boussole est à l'écran sans interruption, et combien de fois elle est apparue
 *  sans que le joueur la touche. */
let boussoleDepuis = 0;
let boussoleVues = 0;
/** L'ancre de la bulle : le DISQUE de la boussole, posé sur la vitre, et non la case qu'il vise —
 *  elle est hors écran par définition, et y ancrer la bulle la ferait pointer vers un point que
 *  personne ne voit. */
let ancreVitre: { x: number; y: number } | null = null;

// ---------- ce que le reste du code appelle ----------

/** Une nouvelle feuille s'ouvre. Les compteurs de session repartent, les acquis ne bougent pas. */
export function coachNouvelleFeuille(): void {
  cacher();
  boussoleDepuis = 0;
  boussoleVues = 0;

  // Le dialogue a déjà été vu, ou le balayage a été appris par la bulle qui l'enseignait avant lui.
  if (acquis.balayer) return;
  // « Première feuille jamais ouverte » se lit sur la PROGRESSION et non sur les drapeaux : un
  // joueur qui revient d'une autre installation a des sauvegardes mais pas de drapeaux, et il
  // n'a pas à être pris pour un débutant. Avec une exception : `poser` sans `balayer`, c'est un
  // joueur de CET appareil qui avait appris le tap par l'ancienne bulle et attendait encore celle
  // du balayage. Elle n'existe plus ; il reçoit le dialogue à la place.
  if (!acquis.poser && progressIndex().size > 0) return;
  armerPoser();
}

/**
 * Le dialogue des deux gestes attend que la feuille soit VISIBLE.
 *
 * startPlay() tourne au démarrage, avant openHome() : sans cette attente, le dialogue s'ouvrirait
 * par-dessus le catalogue, pour une feuille que le joueur n'a pas encore choisie.
 * Le catalogue n'a pas de fermeture qu'on puisse écouter d'ici sans fermer un cycle d'import — on
 * regarde donc l'écran lui-même, ce qui est aussi ce qui rend la condition vraie quel que soit le
 * chemin par lequel on arrive sur la feuille.
 */
function armerPoser(welcome = progressIndex().size === 0): void {
  clearTimeout(minuteur);
  minuteur = window.setTimeout(() => {
    if (acquis.balayer || anim.replay || S.gameOver) return;
    if (document.querySelector('.home.on') || !peutPeindre) return armerPoser(welcome);
    // Le renard ne se présente qu'à un NOUVEAU joueur — lu à l'armement, avant que la première
    // case ne crée une sauvegarde. armerPoser() sert aussi au joueur d'une ancienne version qui
    // avait appris le tap mais pas le balayage (cf. coachNouvelleFeuille) : il a des feuilles,
    // et « Salut, bienvenue ! » serait faux pour lui (revue du 2026-10-31).
    openGestures(welcome);
  }, POSER_MS);
}

/** Le dialogue compte pour les deux leçons qu'il montre, quelle que soit la façon dont on le
 *  ferme : il a été vu en entier, bouton ou « précédent ». */
function openGestures(welcome: boolean): void {
  cacher();
  openGestureDialog(() => {
    coachReussi('poser');
    coachReussi('balayer');
  }, welcome);
}

/**
 * Un tap PEINDRAIT-IL, à l'échelle où la feuille est en ce moment ? Posé par le rendu à chaque
 * image (voir son appel dans render()). Dézoomé, un tap cadre la zone au lieu de la remplir : la
 * première leçon attend donc, plutôt que d'annoncer un geste que le jeu ne fera pas.
 *
 * La dépendance va dans ce sens et pas dans l'autre — coach.ts ne connaît ni render.ts ni ui.ts,
 * c'est ce qui garantit qu'il se débranche d'une ligne.
 */
let peutPeindre = false;
export function coachPeutPeindre(v: boolean): void {
  peutPeindre = v;
}

/**
 * Ce que la boussole montre à cette image, ou null si elle n'est pas affichée. Appelé à chaque
 * image par drawGuide() : deux comparaisons quand il n'y a rien à faire, et c'est le seul endroit
 * du code qui sache à la fois que la flèche est là et où elle est.
 */
export function coachBoussole(g: { x: number; y: number } | null): void {
  if (!g) {
    if (boussoleDepuis) boussoleVues++;
    boussoleDepuis = 0;
    if (visible === 'boussole') {
      cacher();
      if (boussoleVues >= BOUSSOLE_APPARITIONS) acquis.boussole = true;
    }
    return;
  }
  if (acquis.boussole) return;
  const t0 = performance.now();
  if (!boussoleDepuis) boussoleDepuis = t0;
  else if (visible === null && t0 - boussoleDepuis > BOUSSOLE_MS) montrer('boussole', g);
  if (visible === 'boussole') {
    ancreVitre = g;
    placer();
  }
}

/** Le geste a réussi : la leçon s'efface et ne revient jamais. Appelable à tout moment, y compris
 *  si rien n'est affiché — c'est ce qui permet aux appelants de ne rien savoir de l'état. */
export function coachReussi(l: Lecon): void {
  if (acquis[l]) return;
  acquis[l] = true;
  ecrire();
  if (visible === l) cacher();
}

/** « Revoir l'initiation », dans Réglages (cf. restartIntro dans intro.ts) : les leçons repartent
 *  à zéro et le dialogue s'arme comme pour un nouveau joueur — page d'accueil du renard comprise —,
 *  sur la pièce de l'œil que main.ts vient d'ouvrir. armerPoser() attend lui-même que le
 *  catalogue soit refermé et la feuille peignable. */
export function coachReplayIntro(): void {
  acquis = { poser: false, balayer: false, boussole: false };
  ecrire();
  armerPoser(true);
}

/** « Revoir les gestes », dans Réglages. Le dialogue des deux gestes s'ouvre TOUT DE SUITE,
 *  par-dessus les réglages : attendre la prochaine feuille ne marchait pas pour lui, puisqu'il ne
 *  s'arme que pour un joueur sans aucune progression — et celui qui cherche cette rangée en a.
 *  Les trois drapeaux repartent à zéro ; le dialogue refermé rend les deux premiers, et la bulle
 *  de la boussole revient à la prochaine feuille. */
export function coachShowGesturesAgain(): void {
  acquis = { poser: false, balayer: false, boussole: false };
  ecrire();
  openGestures(false);
}

// ---------- l'affichage ----------

function montrer(l: Lecon, g: { x: number; y: number }): void {
  // Jamais par-dessus le rejeu ni sur une feuille terminée : ces deux moments ont leur propre
  // mise en scène, et un conseil y arriverait comme un cheveu sur la soupe.
  if (visible !== null || acquis[l] || anim.replay || S.gameOver || !S.target.length) return;
  if (document.querySelector('.home.on')) return;
  visible = l;
  ancreVitre = g;

  const boite = document.querySelector('.sheetbox');
  if (!boite) return;
  elt = document.createElement('div');
  elt.className = 'coach coach-bulle';
  // `role="status"` et `aria-live` : sans eux, un lecteur d'écran ne saura jamais qu'une phrase
  // vient d'arriver — elle n'a pas le focus et n'en prendra pas, puisqu'elle n'interrompt rien.
  elt.setAttribute('role', 'status');
  elt.setAttribute('aria-live', 'polite');
  if (noMotion()) elt.classList.add('coach-fixe');

  const texte = document.createElement('span');
  texte.className = 'coach-t';
  texte.innerHTML = t('coach.boussole');
  elt.appendChild(texte);

  const croix = document.createElement('button');
  croix.className = 'coach-x';
  croix.type = 'button';
  croix.title = t('coach.close');
  croix.setAttribute('aria-label', t('coach.close'));
  croix.textContent = '✕';
  // Fermer vaut acquisition : celui qui chasse un conseil ne veut pas le revoir demain.
  croix.onclick = () => coachReussi(l);
  elt.appendChild(croix);

  boite.appendChild(elt);
  placer();
}

function cacher(): void {
  clearTimeout(minuteur);
  elt?.remove();
  elt = null;
  visible = null;
  ancreVitre = null;
}

/**
 * Place la bulle. Elle suit le disque de la boussole, donc elle se replace à chaque image tant
 * qu'elle est là.
 *
 * EN POSITION ABSOLUE DANS .sheetbox, et c'est la seule chose à ne pas changer ici : inséré dans
 * le flux entre la feuille et la palette, le conseil changerait la hauteur offerte au canevas et
 * ferait sauter le dessin à son apparition puis à sa disparition (cf. afterLayout dans ui.ts).
 */
function placer(): void {
  if (!elt || !ancreVitre) return;
  const { x, y } = ancreVitre;
  const l = elt.offsetWidth;
  const h = elt.offsetHeight;
  const m = 8;
  // Au-dessus de l'ancre si la place y est, en dessous sinon : une bulle qui déborde du haut de
  // la feuille se ferait couper par la barre de jeu.
  // L'écart à l'ancre : le rayon du disque, pour que la bulle ne recouvre pas ce qu'elle désigne.
  const ecart = 30;
  const dessus = y - h - ecart >= m;
  const gauche = Math.max(m, Math.min(S.availW - l - m, x - l / 2));
  elt.style.left = `${gauche}px`;
  elt.style.top = `${dessus ? y - h - ecart : Math.min(S.availH - h - m, y + ecart)}px`;
  // La pointe suit l'ancre À L'INTÉRIEUR de la bulle : celle-ci est ramenée dans l'écran quand
  // elle déborderait, et une pointe restée au milieu désignerait alors autre chose que ce dont
  // on parle. Bornée pour ne pas sortir de l'arrondi des coins.
  elt.style.setProperty('--pointe', `${Math.max(14, Math.min(l - 14, x - gauche))}px`);
  elt.classList.toggle('coach-sous', !dessus);
}
